import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFilterValueCounts } from '../useFilterValueCounts'
import { MultiSelectCommandList } from './MultiSelectCommandList'
import type { ValuePickerProps } from './types'
import { useMembers } from '@/lib/db/rxdb/hooks'

/** Workspace members, for Assignee and Created by. */
function MemberFilterPicker({
  selectedValues,
  onToggleValue,
  fieldKey,
}: ValuePickerProps & { fieldKey: 'assignee' | 'createdBy' }) {
  const { t } = useTranslation('tasks')
  const { members } = useMembers()
  const counts = useFilterValueCounts(fieldKey)

  const options = useMemo(
    () => [
      // Tasks with no assignee store an empty userId (see the assignee field).
      ...(fieldKey === 'assignee'
        ? [
            {
              value: '',
              label: t('filters.unassigned'),
              icon: (
                <span className="flex size-4 shrink-0 items-center justify-center rounded-full border border-dashed border-muted-foreground/50" />
              ),
            },
          ]
        : []),
      ...[...members]
        .sort((a, b) =>
          (a.name ?? a.email ?? '').localeCompare(
            b.name ?? b.email ?? '',
          ),
        )
        .map((m) => {
          const name = m.name ?? m.email ?? 'Unknown'
          return {
            value: m.userId,
            label: name,
            icon: (
              <span className="flex size-4 items-center justify-center rounded-full bg-muted text-[9px] font-medium shrink-0">
                {name[0]?.toUpperCase() ?? ''}
              </span>
            ),
          }
        }),
    ],
    [members, t, fieldKey],
  )

  return (
    <MultiSelectCommandList
      options={options}
      selectedValues={selectedValues}
      onToggle={onToggleValue}
      searchPlaceholder={t('filters.search.members')}
      counts={counts}
    />
  )
}

export function AssigneeFilterPicker(props: ValuePickerProps) {
  return <MemberFilterPicker {...props} fieldKey="assignee" />
}

export function CreatedByFilterPicker(props: ValuePickerProps) {
  return <MemberFilterPicker {...props} fieldKey="createdBy" />
}
